import React, { useState, useMemo } from 'react';
import { Sparkles, Upload, ChevronDown, Check, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

interface SourceField {
  name: string;
  samples: string[];
}

interface MappingSuggestion {
  source_field: string;
  target_field: string;
  confidence: number;
  transform_suggestion?: string;
  reasoning: string;
}

interface SuggestResponse {
  suggestions: MappingSuggestion[];
  target_product: string;
  target_entity: string;
  ai_powered: boolean;
}

const PRODUCTS: Record<string, string[]> = {
  psa: ['ticket', 'contact', 'company', 'time_entry'],
  crm: ['contact', 'company'],
  cmdb: ['configuration_item', 'flexible_asset', 'password'],
  books: ['customer', 'invoice'],
};

export default function AIMapperPage() {
  const [rawInput, setRawInput] = useState('');
  const [targetProduct, setTargetProduct] = useState('psa');
  const [targetEntity, setTargetEntity] = useState('ticket');
  const [suggestions, setSuggestions] = useState<MappingSuggestion[]>([]);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [aiPowered, setAiPowered] = useState(false);

  const entities = PRODUCTS[targetProduct] || [];

  // Parse source fields from raw input (TSV/CSV header + sample rows)
  const sourceFields = useMemo((): SourceField[] => {
    if (!rawInput.trim()) return [];
    const lines = rawInput.trim().split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return [];

    const delimiter = lines[0].includes('\t') ? '\t' : ',';
    const headers = lines[0].split(delimiter).map(h => h.replace(/^["']|["']$/g, '').trim());
    const sampleRows = lines.slice(1, 4); // up to 3 sample rows

    return headers.filter(Boolean).map((name, i) => ({
      name,
      samples: sampleRows
        .map(row => {
          const cols = row.split(delimiter);
          return (cols[i] || '').replace(/^["']|["']$/g, '').trim();
        })
        .filter(Boolean),
    }));
  }, [rawInput]);

  async function handleSuggest() {
    if (sourceFields.length === 0) {
      setError('Paste or type field names (one per column in header row) with optional sample data rows below.');
      return;
    }

    setLoading(true);
    setError('');
    setSuggestions([]);
    setOverrides({});
    setConfirmed(false);

    try {
      const res = await api.post<SuggestResponse>('/ai-mapper/suggest', {
        source_fields: sourceFields,
        target_product: targetProduct,
        target_entity: targetEntity,
      });
      setSuggestions(res.suggestions);
      setAiPowered(res.ai_powered);
    } catch (err: any) {
      setError(err.message || 'Failed to get AI suggestions');
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    const finalMappings = suggestions.map(s => ({
      source_field: s.source_field,
      target_field: overrides[s.source_field] || s.target_field,
    })).filter(m => m.target_field);

    if (finalMappings.length === 0) {
      setError('No mappings to confirm.');
      return;
    }

    setConfirming(true);
    setError('');

    try {
      await api.post('/ai-mapper/confirm', {
        mappings: finalMappings,
        connector_type: 'manual',
        target_product: targetProduct,
        target_entity: targetEntity,
      });
      setConfirmed(true);
    } catch (err: any) {
      setError(err.message || 'Failed to confirm mappings');
    } finally {
      setConfirming(false);
    }
  }

  function getConfidenceColor(c: number): string {
    if (c >= 0.8) return '#22c55e';
    if (c >= 0.5) return '#f59e0b';
    return '#ef4444';
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Sparkles size={28} color={ACCENT} />
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>AI Data Mapper</h1>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
            AI-powered field mapping for data imports and migrations
          </p>
        </div>
      </div>

      {/* Input Section */}
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        marginBottom: '1rem',
      }}>
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 600 }}>
          Source Data
        </h2>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem', color: '#6b7280' }}>
          Paste your CSV/TSV header row, optionally followed by 1-3 sample data rows.
        </p>
        <textarea
          value={rawInput}
          onChange={e => setRawInput(e.target.value)}
          placeholder={'name,email,phone,company\nJohn Doe,john@acme.com,555-1234,Acme Corp\nJane Smith,jane@widgets.io,555-5678,Widgets Inc'}
          style={{
            width: '100%',
            minHeight: 120,
            padding: '0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
        {sourceFields.length > 0 && (
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
            Detected {sourceFields.length} field{sourceFields.length !== 1 ? 's' : ''}: {sourceFields.map(f => f.name).join(', ')}
          </p>
        )}
      </div>

      {/* Target Selection */}
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '1.5rem',
        marginBottom: '1rem',
        display: 'flex',
        gap: '1rem',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem' }}>
            Target Product
          </label>
          <div style={{ position: 'relative' }}>
            <select
              value={targetProduct}
              onChange={e => {
                setTargetProduct(e.target.value);
                const first = PRODUCTS[e.target.value]?.[0] || '';
                setTargetEntity(first);
                setSuggestions([]);
              }}
              style={{
                width: '100%',
                padding: '0.5rem 2rem 0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                appearance: 'none',
                backgroundColor: '#fff',
              }}
            >
              {Object.keys(PRODUCTS).map(p => (
                <option key={p} value={p}>{p.toUpperCase()}</option>
              ))}
            </select>
            <ChevronDown size={16} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }} />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem' }}>
            Target Entity
          </label>
          <div style={{ position: 'relative' }}>
            <select
              value={targetEntity}
              onChange={e => {
                setTargetEntity(e.target.value);
                setSuggestions([]);
              }}
              style={{
                width: '100%',
                padding: '0.5rem 2rem 0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                appearance: 'none',
                backgroundColor: '#fff',
              }}
            >
              {entities.map(e => (
                <option key={e} value={e}>{e.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
            <ChevronDown size={16} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }} />
          </div>
        </div>

        <button
          onClick={handleSuggest}
          disabled={loading || sourceFields.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1.25rem',
            backgroundColor: loading || sourceFields.length === 0 ? '#d1d5db' : ACCENT,
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: loading || sourceFields.length === 0 ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />}
          {loading ? 'AI is analyzing your data...' : 'AI Suggest Mappings'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '0.375rem',
          marginBottom: '1rem',
          color: '#dc2626',
          fontSize: '0.875rem',
        }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Results */}
      {suggestions.length > 0 && (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          marginBottom: '1rem',
        }}>
          <div style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Mapping Suggestions</h2>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                {aiPowered ? 'Powered by THEO AI' : 'Heuristic matching'} — {suggestions.length} mapping{suggestions.length !== 1 ? 's' : ''}
              </p>
            </div>
            {confirmed ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#22c55e', fontSize: '0.875rem', fontWeight: 600 }}>
                <Check size={16} /> Confirmed
              </span>
            ) : (
              <button
                onClick={handleConfirm}
                disabled={confirming}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#22c55e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  cursor: confirming ? 'not-allowed' : 'pointer',
                }}
              >
                {confirming ? <Loader2 size={14} /> : <Check size={14} />}
                Confirm Mappings
              </button>
            )}
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={thStyle}>Source Field</th>
                <th style={thStyle}>Target Field</th>
                <th style={{ ...thStyle, width: 120 }}>Confidence</th>
                <th style={thStyle}>Transform</th>
                <th style={thStyle}>Reasoning</th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map((s, i) => {
                const target = overrides[s.source_field] || s.target_field;
                const conf = s.confidence;
                return (
                  <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>
                      <code style={{ fontSize: '0.8125rem', backgroundColor: '#f3f4f6', padding: '0.125rem 0.375rem', borderRadius: '0.25rem' }}>
                        {s.source_field}
                      </code>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={target}
                          onChange={e => setOverrides(prev => ({ ...prev, [s.source_field]: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '0.375rem 0.5rem',
                            border: `1px solid ${overrides[s.source_field] ? ACCENT : '#d1d5db'}`,
                            borderRadius: '0.25rem',
                            fontSize: '0.8125rem',
                            backgroundColor: overrides[s.source_field] ? '#fff7ed' : '#fff',
                          }}
                        >
                          <option value="">— skip —</option>
                          {/* Show current target first if it's the AI pick */}
                          <option value={s.target_field}>{s.target_field}</option>
                        </select>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{
                          flex: 1,
                          height: 6,
                          backgroundColor: '#e5e7eb',
                          borderRadius: 3,
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${Math.round(conf * 100)}%`,
                            height: '100%',
                            backgroundColor: getConfidenceColor(conf),
                            borderRadius: 3,
                          }} />
                        </div>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: getConfidenceColor(conf), minWidth: 32, textAlign: 'right' }}>
                          {Math.round(conf * 100)}%
                        </span>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {s.transform_suggestion ? (
                        <span style={{ fontSize: '0.75rem', color: '#7c3aed', backgroundColor: '#f5f3ff', padding: '0.125rem 0.5rem', borderRadius: '0.25rem' }}>
                          {s.transform_suggestion}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }} title={s.reasoning}>
                        {s.reasoning.length > 60 ? s.reasoning.slice(0, 57) + '...' : s.reasoning}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Spin keyframes via style tag */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '0.625rem 1rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const tdStyle: React.CSSProperties = {
  padding: '0.625rem 1rem',
  fontSize: '0.875rem',
};
